import { useState, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useCadence } from '@/contexts/CadenceContext'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Play,
  Pause,
  Users,
  Upload,
  Pencil,
  X,
  ExternalLink,
  AlertTriangle,
  Cloud,
} from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { ImportLeadsDialog } from '@/components/ImportLeadsDialog'
import { PermissionGate } from '@/components/PermissionGate'
import { LEAD_STATUS_CONFIG, type LeadStatus, type Lead } from '@/types'
import { useSalesforceConnection } from '@/hooks/useSalesforceConnection'
import { invokeEdgeOrThrow } from '@/lib/edge-functions'
import { toast } from 'sonner'
import { PageTransition } from '@/components/PageTransition'

interface LeadFormData {
  first_name: string
  last_name: string
  email: string
  company: string
  title: string
  linkedin_url: string
  phone: string
}

const initialFormData: LeadFormData = {
  first_name: '',
  last_name: '',
  email: '',
  company: '',
  title: '',
  linkedin_url: '',
  phone: '',
}

export function Leads() {
  const { user, session } = useAuth()
  const {
    leads,
    cadences,
    isLoading,
    createLead,
    updateLead,
    deleteLead,
    assignLeadToCadence,
    removeLeadFromCadence,
    updateLeadCadenceStatus,
  } = useCadence()
  const queryClient = useQueryClient()
  const { status: sfStatus } = useSalesforceConnection()
  const [pushingLeadId, setPushingLeadId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkPushing, setBulkPushing] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('company') || '')
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false)

  // Sync search query from URL params (e.g., when navigating from Company Registry)
  useEffect(() => {
    const company = searchParams.get('company')
    if (company) {
      setSearchQuery(company)
      // Clear the URL param after applying
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])
  const [isEditLeadOpen, setIsEditLeadOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [isAssignOpen, setIsAssignOpen] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<LeadFormData>(initialFormData)
  const [selectedCadenceId, setSelectedCadenceId] = useState<string>('none')
  const [duplicateLead, setDuplicateLead] = useState<Lead | null>(null)
  const [pendingLeadData, setPendingLeadData] = useState<LeadFormData | null>(null)

  // Filter leads based on search query with partial matching
  const filteredLeads = useMemo(() => {
    if (!searchQuery.trim()) return leads

    const query = searchQuery.toLowerCase().trim()
    return leads.filter((lead) => {
      const fullName = `${lead.first_name} ${lead.last_name}`.toLowerCase()
      return (
        fullName.includes(query) ||
        lead.first_name.toLowerCase().includes(query) ||
        lead.last_name.toLowerCase().includes(query) ||
        lead.email?.toLowerCase().includes(query) ||
        lead.company?.toLowerCase().includes(query) ||
        lead.title?.toLowerCase().includes(query)
      )
    })
  }, [leads, searchQuery])

  // Check for duplicate email in same cadence — checks ALL memberships,
  // not just the flattened cadence_id (a lead can be in several cadences)
  const checkDuplicate = (email: string, cadenceId: string, excludeLeadId?: string): Lead | null => {
    if (!email || !cadenceId) return null
    return leads.find(
      (lead) =>
        lead.email?.toLowerCase() === email.toLowerCase() &&
        (lead.cadence_memberships?.some((m) => m.cadence_id === cadenceId) ?? lead.cadence_id === cadenceId) &&
        lead.id !== excludeLeadId
    ) || null
  }

  const resetForm = () => {
    setFormData(initialFormData)
    setSelectedCadenceId('')
    setDuplicateLead(null)
    setPendingLeadData(null)
  }

  const handleOpenAddLead = () => {
    resetForm()
    setIsAddLeadOpen(true)
  }

  const handleOpenEditLead = (lead: Lead) => {
    setSelectedLeadId(lead.id)
    setFormData({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email || '',
      company: lead.company || '',
      title: lead.title || '',
      linkedin_url: lead.linkedin_url || '',
      phone: lead.phone || '',
    })
    setSelectedCadenceId(lead.cadence_id || '')
    setIsEditLeadOpen(true)
  }

  const handleSaveLead = async (isEdit: boolean = false) => {
    if (!user || !formData.first_name || !formData.last_name || !formData.email) return

    // Check for duplicate if cadence is selected
    if (selectedCadenceId && selectedCadenceId !== 'none') {
      const duplicate = checkDuplicate(
        formData.email,
        selectedCadenceId,
        isEdit ? selectedLeadId! : undefined
      )
      if (duplicate) {
        setDuplicateLead(duplicate)
        setPendingLeadData(formData)
        setIsDuplicateDialogOpen(true)
        return
      }
    }

    await performSave(isEdit, formData)
  }

  const performSave = async (isEdit: boolean, data: LeadFormData) => {
    if (!user) return
    setSaving(true)

    try {
      if (isEdit && selectedLeadId) {
        await updateLead(selectedLeadId, {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email || null,
          company: data.company || null,
          title: data.title || null,
          linkedin_url: data.linkedin_url || null,
          phone: data.phone || null,
        })

        // Update cadence assignment if changed — check ALL memberships so we don't
        // re-assign (and reset progress in) a cadence the lead is already in
        const currentLead = leads.find((l) => l.id === selectedLeadId)
        const effectiveCadenceId = selectedCadenceId === 'none' ? null : selectedCadenceId
        if (effectiveCadenceId) {
          const alreadyInTarget = (currentLead?.cadence_memberships ?? []).some(
            (m) => m.cadence_id === effectiveCadenceId
          )
          if (!alreadyInTarget) {
            await assignLeadToCadence(selectedLeadId, effectiveCadenceId)
          }
        } else if (currentLead?.cadence_id) {
          await removeLeadFromCadence(selectedLeadId, currentLead.cadence_id)
        }

        setIsEditLeadOpen(false)
      } else {
        const newLead = await createLead({
          owner_id: user.id,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email || null,
          company: data.company || null,
          title: data.title || null,
          linkedin_url: data.linkedin_url || null,
          phone: data.phone || null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          industry: null,
          website: null,
          company_linkedin_url: null,
          annual_revenue: null,
          total_funding: null,
          latest_funding: null,
          latest_funding_amount: null,
          department: null,
          corporate_phone: null,
          personal_phone: null,
        })

        // Assign to cadence if selected
        if (newLead && selectedCadenceId && selectedCadenceId !== 'none') {
          await assignLeadToCadence(newLead.id, selectedCadenceId)
        }

        setIsAddLeadOpen(false)
      }

      resetForm()
    } catch (error) {
      console.error('Error saving lead:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save lead')
    } finally {
      setSaving(false)
    }
  }

  const handleReplaceDuplicate = async () => {
    if (!duplicateLead || !pendingLeadData) return
    setSaving(true)

    try {
      // Update the existing duplicate with new data
      await updateLead(duplicateLead.id, {
        first_name: pendingLeadData.first_name,
        last_name: pendingLeadData.last_name,
        email: pendingLeadData.email || null,
        company: pendingLeadData.company || null,
        title: pendingLeadData.title || null,
        linkedin_url: pendingLeadData.linkedin_url || null,
        phone: pendingLeadData.phone || null,
      })

      setIsDuplicateDialogOpen(false)
      setIsAddLeadOpen(false)
      setIsEditLeadOpen(false)
      resetForm()
    } catch (error) {
      console.error('Error replacing duplicate lead:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to replace lead')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelDuplicate = () => {
    setIsDuplicateDialogOpen(false)
  }

  const handleDeleteLead = async () => {
    if (!leadToDelete) return
    await deleteLead(leadToDelete)
    setIsDeleteConfirmOpen(false)
    setLeadToDelete(null)
  }

  const openDeleteConfirm = (leadId: string) => {
    setLeadToDelete(leadId)
    setIsDeleteConfirmOpen(true)
  }

  // Pause/resume update cadence_leads — updateLead drops `status` on purpose
  // (the leads table has no status column), so it was a silent no-op here.
  // Each action takes an explicit cadenceId: the flattened lead.cadence_id
  // falls back to memberships[0] (arbitrary PostgREST order) when no membership
  // is 'active', so acting on it could pause/resume/remove the wrong cadence.
  const handlePauseLead = async (id: string, cadenceId: string) => {
    await updateLeadCadenceStatus(id, cadenceId, 'paused')
  }

  const handleResumeLead = async (id: string, cadenceId: string) => {
    await updateLeadCadenceStatus(id, cadenceId, 'active')
  }

  const openAssignDialog = (leadId: string) => {
    setSelectedLeadId(leadId)
    setIsAssignOpen(true)
  }

  const handleAssign = async (cadenceId: string) => {
    if (!selectedLeadId) return
    try {
      const { alreadyInCadence, reactivated } = await assignLeadToCadence(selectedLeadId, cadenceId)
      if (reactivated) {
        // Membership existed but was removed/completed — restarted as 'active'
        // at the step after its last sent one (BUG-093 tail-restart semantics)
        toast.success('El lead estaba removido o completado en esa cadencia — reactivado desde su primer paso pendiente')
      } else if (alreadyInCadence) {
        // Membership with live progress (or nothing left to re-send) —
        // preserved as-is, nothing was overwritten (assignLeadToCadence guard)
        toast.info('El lead ya estaba en esa cadencia con progreso — se conserva su progreso y estado')
      }
      setIsAssignOpen(false)
      setSelectedLeadId(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to assign lead to cadence')
    }
  }

  const handleRemoveFromCadence = async (leadId: string, cadenceId: string) => {
    await removeLeadFromCadence(leadId, cadenceId)
  }

  const handlePushToSalesforce = async (lead: Lead) => {
    if (!session?.access_token) return
    setPushingLeadId(lead.id)
    try {
      const result = await invokeEdgeOrThrow<{
        success: boolean
        duplicate?: boolean
        salesforceLeadId?: string
        salesforceName?: string
        message?: string
      }>('salesforce-push-lead', { leadId: lead.id })

      if (result.duplicate) {
        toast.warning(result.message || 'Lead already exists in Salesforce')
      } else if (result.success) {
        toast.success(`${lead.first_name} ${lead.last_name} pushed to Salesforce`)
        queryClient.invalidateQueries({ queryKey: ['leads'] })
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to push lead to Salesforce')
    } finally {
      setPushingLeadId(null)
    }
  }

  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredLeads.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredLeads.map((l) => l.id)))
    }
  }

  // Leads eligible for SF push among selected
  const selectedPushable = useMemo(
    () => filteredLeads.filter((l) => selectedIds.has(l.id) && l.email && !l.salesforce_lead_id),
    [filteredLeads, selectedIds]
  )

  const handleBulkPushToSalesforce = async () => {
    if (!session?.access_token || selectedPushable.length === 0) return
    setBulkPushing(true)
    try {
      const result = await invokeEdgeOrThrow<{
        success: boolean
        total: number
        pushed: number
        duplicates: number
        failed: number
      }>(
        'salesforce-push-lead',
        { leadIds: selectedPushable.map((l) => l.id) },
        { timeoutMs: 120000 }
      )

      const parts: string[] = []
      if (result.pushed > 0) parts.push(`${result.pushed} pushed`)
      if (result.duplicates > 0) parts.push(`${result.duplicates} duplicates`)
      if (result.failed > 0) parts.push(`${result.failed} failed`)

      if (result.pushed > 0) {
        toast.success(`Salesforce: ${parts.join(', ')}`)
      } else {
        toast.warning(`Salesforce: ${parts.join(', ')}`)
      }

      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setSelectedIds(new Set())
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to push leads to Salesforce')
    } finally {
      setBulkPushing(false)
    }
  }

  const getStatusBadgeVariant = (status: LeadStatus) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'failed':
        return 'destructive'
      case 'paused':
        return 'secondary'
      case 'sent':
        return 'info'
      default:
        return 'outline'
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
  }

  // Validation for required fields
  const isFormValid = !!(
    formData.first_name.trim() &&
    formData.last_name.trim() &&
    formData.email.trim() &&
    formData.company.trim() &&
    formData.title.trim() &&
    formData.linkedin_url.trim()
  )

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <PageTransition className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-[28px] font-bold tracking-tight font-heading">Leads</h1>
          <p className="text-muted-foreground">Manage your contacts and prospects</p>
        </div>
        <div className="flex gap-2">
          <PermissionGate permission="leads_import">
            <Button variant="outline" onClick={() => setIsImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import Leads
            </Button>
          </PermissionGate>
          <PermissionGate permission="leads_create">
            <Button onClick={handleOpenAddLead}>
              <Plus className="mr-2 h-4 w-4" />
              Create Lead
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Leads ({leads.length})</CardTitle>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, email, empresa, título..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium">Sin leads encontrados</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {searchQuery ? 'Intenta otra búsqueda' : 'Agrega tu primer lead para comenzar'}
              </p>
              {!searchQuery && (
                <PermissionGate permission="leads_create">
                  <Button onClick={handleOpenAddLead}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Lead
                  </Button>
                </PermissionGate>
              )}
            </div>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div className="mb-4 flex items-center gap-3 rounded-lg border bg-muted/50 px-4 py-2">
                  <span className="text-sm font-medium">
                    {selectedIds.size} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear
                  </Button>
                  {sfStatus.isConnected && selectedPushable.length > 0 && (
                    <Button
                      size="sm"
                      onClick={handleBulkPushToSalesforce}
                      disabled={bulkPushing}
                    >
                      <Cloud className="mr-2 h-4 w-4" />
                      {bulkPushing
                        ? 'Pushing...'
                        : `Push to Salesforce (${selectedPushable.length})`}
                    </Button>
                  )}
                </div>
              )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-3 pr-2 w-10">
                      <Checkbox
                        checked={filteredLeads.length > 0 && selectedIds.size === filteredLeads.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Company</th>
                    <th className="pb-3 font-medium">Title</th>
                    <th className="pb-3 font-medium">LinkedIn</th>
                    <th className="pb-3 font-medium">Phone</th>
                    <th className="pb-3 font-medium">Cadence</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredLeads.map((lead) => {
                    // Show every cadence the lead belongs to, not an arbitrary one
                    const memberships = lead.cadence_memberships ?? []
                    const leadCadenceNames = memberships
                      .map((m) => cadences.find((c) => c.id === m.cadence_id)?.name)
                      .filter((name): name is string => !!name)
                    const cadenceName = (id: string) =>
                      cadences.find((c) => c.id === id)?.name ?? 'cadence'
                    // Deterministic action targets — never the flattened
                    // lead.cadence_id, whose fallback is memberships[0]
                    // (arbitrary PostgREST order) when none is 'active'.
                    const activeMemberships = memberships.filter((m) => m.status === 'active')
                    // 'paused' + current_step_id=null is a removed ("take out")
                    // membership: resuming it would revive a cadence with no step
                    const resumableMemberships = memberships.filter(
                      (m) => m.status === 'paused' && m.current_step_id !== null
                    )
                    const removableMemberships = memberships.filter(
                      (m) => m.status !== 'completed' && !(m.status === 'paused' && m.current_step_id === null)
                    )
                    return (
                      <tr key={lead.id} className="text-sm">
                        <td className="py-3 pr-2 w-10">
                          <Checkbox
                            checked={selectedIds.has(lead.id)}
                            onCheckedChange={() => toggleSelect(lead.id)}
                          />
                        </td>
                        <td className="py-3">
                          <p className="font-medium flex items-center gap-1.5">
                            {lead.first_name} {lead.last_name}
                            {lead.salesforce_lead_id && (
                              <span title="Pushed to Salesforce">
                                <Cloud className="h-3.5 w-3.5 text-blue-500" />
                              </span>
                            )}
                          </p>
                        </td>
                        <td className="py-3">{lead.email || '-'}</td>
                        <td className="py-3">{lead.company || '-'}</td>
                        <td className="py-3">{lead.title || '-'}</td>
                        <td className="py-3">
                          {lead.linkedin_url ? (
                            <a
                              href={lead.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              Profile
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-3">{lead.phone || '-'}</td>
                        <td className="py-3">
                          {leadCadenceNames.length > 0 ? (
                            <span className="text-sm">{leadCadenceNames.join(', ')}</span>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAssignDialog(lead.id)}
                            >
                              Assign
                            </Button>
                          )}
                        </td>
                        <td className="py-3">
                          <Badge
                            variant={getStatusBadgeVariant(lead.status || 'pending') as 'default'}
                          >
                            {LEAD_STATUS_CONFIG[lead.status || 'pending'].label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenEditLead(lead)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              {removableMemberships.length === 0 ? (
                                <DropdownMenuItem onClick={() => openAssignDialog(lead.id)}>
                                  <Play className="mr-2 h-4 w-4" />
                                  Assign to Cadence
                                </DropdownMenuItem>
                              ) : (
                                removableMemberships.map((m) => (
                                  <DropdownMenuItem
                                    key={`remove-${m.cadence_id}`}
                                    onClick={() => handleRemoveFromCadence(lead.id, m.cadence_id)}
                                  >
                                    <Pause className="mr-2 h-4 w-4" />
                                    {removableMemberships.length > 1
                                      ? `Remove from ${cadenceName(m.cadence_id)}`
                                      : 'Remove from Cadence'}
                                  </DropdownMenuItem>
                                ))
                              )}
                              {activeMemberships.map((m) => (
                                <DropdownMenuItem
                                  key={`pause-${m.cadence_id}`}
                                  onClick={() => handlePauseLead(lead.id, m.cadence_id)}
                                >
                                  <Pause className="mr-2 h-4 w-4" />
                                  {activeMemberships.length > 1
                                    ? `Pause — ${cadenceName(m.cadence_id)}`
                                    : 'Pause'}
                                </DropdownMenuItem>
                              ))}
                              {resumableMemberships.map((m) => (
                                <DropdownMenuItem
                                  key={`resume-${m.cadence_id}`}
                                  onClick={() => handleResumeLead(lead.id, m.cadence_id)}
                                >
                                  <Play className="mr-2 h-4 w-4" />
                                  {resumableMemberships.length > 1
                                    ? `Resume — ${cadenceName(m.cadence_id)}`
                                    : 'Resume'}
                                </DropdownMenuItem>
                              ))}
                              {sfStatus.isConnected && lead.email && !lead.salesforce_lead_id && (
                                <DropdownMenuItem
                                  onClick={() => handlePushToSalesforce(lead)}
                                  disabled={pushingLeadId === lead.id}
                                >
                                  <Cloud className="mr-2 h-4 w-4" />
                                  {pushingLeadId === lead.id ? 'Pushing...' : 'Push to Salesforce'}
                                </DropdownMenuItem>
                              )}
                              <PermissionGate permission="leads_delete">
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => openDeleteConfirm(lead.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </PermissionGate>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Lead Dialog */}
      <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Lead</DialogTitle>
            <DialogDescription>
              Enter the lead's information. Required fields are marked with *.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="john@company.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company *</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                  placeholder="Acme Inc"
                />
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Sales Manager"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>LinkedIn URL *</Label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, linkedin_url: e.target.value }))}
                placeholder="https://linkedin.com/in/johndoe"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label>Add to Cadence</Label>
              <Select value={selectedCadenceId} onValueChange={setSelectedCadenceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cadencia (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No cadence --</SelectItem>
                  {cadences
                    .filter((c) => c.status === 'active')
                    .map((cadence) => (
                      <SelectItem key={cadence.id} value={cadence.id}>
                        {cadence.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddLeadOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSaveLead(false)} disabled={saving || !isFormValid}>
              {saving ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={isEditLeadOpen} onOpenChange={setIsEditLeadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Lead</DialogTitle>
            <DialogDescription>
              Update the lead's information. Required fields are marked with *.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company *</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>LinkedIn URL *</Label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, linkedin_url: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Cadence</Label>
              <Select value={selectedCadenceId} onValueChange={setSelectedCadenceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cadencia (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No cadence --</SelectItem>
                  {cadences
                    .filter((c) => c.status === 'active')
                    .map((cadence) => (
                      <SelectItem key={cadence.id} value={cadence.id}>
                        {cadence.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditLeadOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSaveLead(true)} disabled={saving || !isFormValid}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Cadence Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign to Cadence</DialogTitle>
            <DialogDescription>Selecciona una cadencia para este lead</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select onValueChange={handleAssign}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar cadencia" />
              </SelectTrigger>
              <SelectContent>
                {cadences
                  .filter((c) => c.status === 'active')
                  .map((cadence) => (
                    <SelectItem key={cadence.id} value={cadence.id}>
                      {cadence.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {cadences.filter((c) => c.status === 'active').length === 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                No active cadences available. Activate a cadence first.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Duplicate Lead Found
            </DialogTitle>
            <DialogDescription>
              A lead with email "{pendingLeadData?.email}" already exists in this cadence.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {duplicateLead && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <p className="font-medium">
                  {duplicateLead.first_name} {duplicateLead.last_name}
                </p>
                <p className="text-sm text-muted-foreground">{duplicateLead.email}</p>
                <p className="text-sm text-muted-foreground">
                  {duplicateLead.company} - {duplicateLead.title}
                </p>
              </div>
            )}
            <p className="mt-4 text-sm text-muted-foreground">
              Would you like to replace the existing lead with the new information, or cancel?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDuplicate}>
              Cancel
            </Button>
            <Button onClick={handleReplaceDuplicate} disabled={saving}>
              {saving ? 'Replacing...' : 'Replace Existing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Lead
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar este lead? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteLead}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportLeadsDialog open={isImportOpen} onOpenChange={setIsImportOpen} />
    </PageTransition>
  )
}
